import React, { useCallback, useMemo, useRef, useState } from "react";
import { useMutation } from "@apollo/client";
import { DataGrid } from "@toddle-edu/ds-data-grid";
import { useTranslation } from "react-i18next";
import _ from "lodash";

import { useContainerWidth } from "Hooks";
import { useDispatch } from "Hooks/useDispatch";
import { setToastMsg } from "Login/modules/LoginModule";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import { updateTranscriptGradingPeriodsMutation } from "../../../../module/AcademicRecordsMutations";
import {
  getOrgTranscriptEntitiesQuery,
  getStudentTranscriptAcademicRecordsQuery,
} from "../../../../module/AcademicRecordsQueries";
import { useCustomModalEntities } from "TranscriptsV3/hooks";
import GenericCustomFieldModal from "TranscriptsV3/components/GenericCustomFieldModal";
import { ASSOCIATION_ENTITY_COLLECTION_KEYS } from "TranscriptsV3/components/GenericCustomFieldModal/modules/constants";
import { showManageEntitiesValidationError } from "../../../ManageEntitiesModal/modules/utils";
import { useGeneralGradingPeriodsSectionData } from "./hooks/useGeneralGradingPeriodsSectionData";
import { computeCustomGradingPeriodsDiff } from "./modules/utils";
import classes from "./GeneralGradingPeriodsSection.scss";

const TEST_ID_PREFIX =
  "transcriptsV3-academicRecords-generalGradingPeriodsSection";

const ConfigureGradingPeriodsModal = ({
  transcriptGradingPeriods,
  onClose,
  onSave,
}) => {
  const { t } = useTranslation(["common", "transcript"]);
  const dispatch = useDispatch();
  const [isSaving, setIsSaving] = useState(false);

  const currentEntities = useMemo(
    () =>
      _.map(
        _.filter(transcriptGradingPeriods, gp => _.get(gp, "isCustom")),
        gp => ({
          id: _.get(gp, "id"),
          name: _.get(gp, "name", ""),
          displaySequence: _.get(gp, "displaySequence"),
          associations: _.get(gp, "associations"),
          isNew: false,
        })
      ),
    [transcriptGradingPeriods]
  );

  const {
    entities,
    setEntities,
    entitiesViewModel,
    validateEntities,
    handleAddEntity,
    addEntityWithValue,
    handleEntityChange,
    handleEntityDelete,
  } = useCustomModalEntities({
    initialEntities: currentEntities,
    initialTab: "CUSTOM_ENTITIES",
    valueKey: "name",
  });

  const handleSave = useCallback(() => {
    if (validateEntities()) {
      showManageEntitiesValidationError(dispatch);
      return;
    }

    const input = computeCustomGradingPeriodsDiff({
      entities,
      currentEntities,
      valueKey: "name",
    });

    if (!input.hasChanges) {
      onClose();
      return;
    }

    setIsSaving(true);
    onSave({
      added: input.added,
      updated: input.updated,
      removed: input.removed,
      onCompleted: onClose,
      onFinally: () => setIsSaving(false),
    });
  }, [entities, currentEntities, validateEntities, onClose, onSave, dispatch]);

  const content = useMemo(
    () => ({
      type: "customGrid",
      config: {
        associationEntityCollectionKey:
          ASSOCIATION_ENTITY_COLLECTION_KEYS.TRANSCRIPT_GRADING_PERIODS,
        deleteTooltipText: t(
          "transcript:disabled_delete_entity_tooltip_with_label",
          { label: t("common:grading_period") }
        ),
        emptyRow: {
          value: "",
          onChange: ({ target: { value } }) => addEntityWithValue(value),
        },
        items: entitiesViewModel,
        onAdd: handleAddEntity,
        onDelete: handleEntityDelete,
        onItemsUpdate: setEntities,
        onValueChange: handleEntityChange,
        placeholder: t("transcript:enter_grading_period_abbreviation"),
        valueKey: "name",
      },
    }),
    [
      addEntityWithValue,
      entitiesViewModel,
      handleAddEntity,
      handleEntityChange,
      handleEntityDelete,
      setEntities,
      t,
    ]
  );

  return (
    <>
      <GenericCustomFieldModal
        testId={`${TEST_ID_PREFIX}-configure-modal`}
        title={t("transcript:configure_custom_list_of_label", {
          label: t("common:grading_period_plural"),
        })}
        subtitle={t("transcript:manage_entities_modal_subtext", {
          label: t("common:grading_period_plural"),
        })}
        onClose={onClose}
        onSave={handleSave}
        isSaving={isSaving}
        content={content}
      />
      {isSaving ? <FullScreenLoader /> : null}
    </>
  );
};

const GeneralGradingPeriodsSection = ({
  academicRecordsData,
  transcriptGradingPeriods,
  getCurrentPeriods,
  onPeriodSelectionChange,
  onSaveGradingPeriods,
  isLoading,
  errorGradeMapIds,
  onClearErrors,
}) => {
  const containerRef = useRef(null);
  const containerWidth = useContainerWidth({ containerRef });
  const dispatch = useDispatch();
  const { t } = useTranslation(["common", "transcript"]);
  const [isConfigureOpen, setIsConfigureOpen] = useState(false);
  const [isCreatingGp, setIsCreatingGp] = useState(false);
  const [createGpMutateFn] = useMutation(
    updateTranscriptGradingPeriodsMutation
  );

  const handleOpenConfigure = useCallback(() => {
    setIsConfigureOpen(true);
  }, []);

  const { headers, gridRows } = useGeneralGradingPeriodsSectionData({
    academicRecordsData,
    transcriptGradingPeriods,
    getCurrentPeriods,
    containerWidth,
    onHeaderIconClick: handleOpenConfigure,
    errorGradeMapIds,
    t,
  });

  const existingGpIds = useMemo(
    () =>
      new Set(_.map(transcriptGradingPeriods, gp => String(_.get(gp, "id")))),
    [transcriptGradingPeriods]
  );

  const createNewGradingPeriods = useCallback(
    async newEntries => {
      const largestDisplaySequence =
        _.maxBy(transcriptGradingPeriods, "displaySequence")
          ?.displaySequence || 0;

      const entries = Object.entries(newEntries);
      const added = _.map(entries, ([, label], index) => ({
        name: label,
        displaySequence: largestDisplaySequence + index + 1,
      }));

      const { data } = await createGpMutateFn({
        variables: { input: { added } },
        refetchQueries: [
          getOrgTranscriptEntitiesQuery,
          getStudentTranscriptAcademicRecordsQuery,
        ],
        awaitRefetchQueries: true,
      });

      const createdRecords = _.get(
        data,
        "transcript.updateTranscriptGradingPeriods.createdRecords",
        []
      );

      dispatch(
        setToastMsg({
          msg: "toastMsgs:entity_updated_successfully",
          locale_params: [
            {
              key: "entity",
              value: "common:grading_period_plural",
              isPlainText: false,
            },
          ],
          type: "success",
        })
      );

      const idMap = {};
      _.forEach(createdRecords, (record, index) => {
        const [tempId] = entries[index];
        idMap[tempId] = String(record.id);
      });

      return idMap;
    },
    [createGpMutateFn, dispatch, transcriptGradingPeriods]
  );

  const handleCellEdit = useCallback(
    async edits => {
      const normalizedEdits = _.castArray(edits);

      for (const edit of normalizedEdits) {
        const colId =
          _.get(edit, "newValue.colId") || _.get(edit, "cellCoods.colId");
        if (colId !== "gradingPeriods") continue;

        const gradeMapId = _.get(edit, "cellCoods.rowId");
        const nextSelectedPeriods = _.get(edit, "newValue.value", []);

        if (!_.isEmpty(nextSelectedPeriods) && onClearErrors) {
          onClearErrors();
        }

        // Detect new GPs: IDs not in any existing GP (custom or toddle-defined)
        const newEntities = {};
        _.forEach(nextSelectedPeriods, ({ id, label }) => {
          if (!existingGpIds.has(String(id))) {
            newEntities[id] = label;
          }
        });

        let tempIdToRealId = {};

        if (!_.isEmpty(newEntities)) {
          setIsCreatingGp(true);
          try {
            tempIdToRealId = await createNewGradingPeriods(newEntities);
          } catch {
            setIsCreatingGp(false);
            return;
          }
          setIsCreatingGp(false);
        }

        const draft = getCurrentPeriods(gradeMapId) || [];
        const draftById = _.keyBy(draft, p => String(p.gradingPeriodId));

        const newPeriods = _.map(nextSelectedPeriods, (period, index) => {
          const tempId = String(period.id);
          const realId = tempIdToRealId[tempId] || tempId;
          const existing = draftById[realId];
          return existing
            ? { ...existing, displaySequence: index + 1 }
            : {
                gradingPeriodId: realId,
                isEnabled: true,
                displaySequence: index + 1,
              };
        });

        onPeriodSelectionChange({
          gradeMapId,
          currentPeriods: newPeriods,
        });
      }
    },
    [
      getCurrentPeriods,
      onPeriodSelectionChange,
      onClearErrors,
      existingGpIds,
      createNewGradingPeriods,
    ]
  );

  const handleCloseConfigure = useCallback(() => {
    setIsConfigureOpen(false);
  }, []);

  const handleSaveGradingPeriods = useCallback(
    ({ added, updated, removed, onCompleted, onFinally }) => {
      if (onSaveGradingPeriods) {
        onSaveGradingPeriods({
          input: { added, updated, removed },
          onCompleted,
          onFinally,
        });
      }
    },
    [onSaveGradingPeriods]
  );

  return (
    <div className={classes.container}>
      <div ref={containerRef} className={classes.gridWrapper}>
        <DataGrid
          headers={headers}
          data={gridRows}
          containerWidth={containerWidth}
          dataGridWidth="100%"
          dataGridHeight="100%"
          onCellEdit={handleCellEdit}
          headerHeight={44}
          rowMarkers="none"
          freezeColumns={1}
          columnsResizable={false}
          showSkeletonLoader={isLoading}
          isViewMode={false}
        />
      </div>
      {isConfigureOpen ? (
        <ConfigureGradingPeriodsModal
          transcriptGradingPeriods={transcriptGradingPeriods}
          onClose={handleCloseConfigure}
          onSave={handleSaveGradingPeriods}
        />
      ) : null}
      {isCreatingGp ? <FullScreenLoader /> : null}
    </div>
  );
};

export default GeneralGradingPeriodsSection;
